/**
 * Kanban Column Handlers
 * Handles undo/redo for Kanban column operations (add, update, delete, reorder)
 */

import type { Operation, NodeOperation, KanbanColumnConfig, ProjectNode } from '@/shared/types/projectData';
import type { OperationHandler } from '../types';

/** Extended operation fields used by kanban column handlers but not on the base NodeOperation type. */
interface KanbanColumnOperation extends NodeOperation {
  migratedTasks?: Array<{
    nodeId: string;
    previousStatus?: string;
    previousCompletionState?: string;
    previousProgress?: number;
    previousViewState?: ProjectNode['viewState'];
    previousColumnId?: string;
    previousOrder?: number;
    nextStatus?: string;
    nextCompletionState?: string;
    nextProgress?: number;
    nextViewState?: ProjectNode['viewState'];
    nextColumnId?: string;
    nextOrder?: number;
  }>;
}

function isKanbanColumnConfig(value: unknown): value is KanbanColumnConfig {
  return Boolean(value)
    && typeof value === 'object'
    && isRecord(value)
    && typeof value.id === 'string'
    && typeof value.title === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function readBeforeColumnData(operation: NodeOperation): KanbanColumnConfig | undefined {
  if (operation.beforeColumnData) return operation.beforeColumnData;
  return isKanbanColumnConfig(operation.beforeState) ? operation.beforeState : undefined;
}

function readBeforeColumnOrder(operation: NodeOperation): string[] | undefined {
  if (operation.beforeColumnOrder) return operation.beforeColumnOrder;
  return readLegacyColumnOrder(operation.beforeState);
}

function readLegacyColumnOrder(value: unknown): string[] | undefined {
  if (!isRecord(value)) return undefined;
  const columnOrder = value.columnOrder;
  return Array.isArray(columnOrder) && columnOrder.every((id) => typeof id === 'string') ? columnOrder : undefined;
}

export function registerKanbanHandlers(
  handlers: Map<string, OperationHandler>,
  applyOp: (op: NodeOperation) => Promise<void>
): void {
  handlers.set('add-column', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      const columnId = op.columnId || op.columnData?.id;
      if (!columnId) return;

      await applyOp({
        type: 'delete-column',
        columnId,
        description: `Undo: ${op.description}`,
        metadata: op.metadata,
        skipHistory: true
      });
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      if (!op.columnData) return;

      await applyOp({
        type: 'add-column',
        columnData: op.columnData,
        columnId: op.columnData.id,
        description: `Redo: ${op.description}`,
        metadata: op.metadata,
        skipHistory: true
      });
    }
  });

  handlers.set('update-column', {
    undo: async (operation: Operation) => {
      const op = operation as KanbanColumnOperation;
      const beforeColumnData = readBeforeColumnData(op);
      if (!op.columnId || !beforeColumnData) return;

      await applyOp({
        type: 'update-column',
        columnId: op.columnId,
        columnData: beforeColumnData,
        description: `Undo: ${op.description}`,
        metadata: op.metadata,
        skipHistory: true
      });
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      if (!op.columnId || !op.columnData) return;

      await applyOp({
        type: 'update-column',
        columnId: op.columnId,
        columnData: op.columnData,
        description: `Redo: ${op.description}`,
        metadata: op.metadata,
        skipHistory: true
      });
    }
  });

  handlers.set('delete-column', {
    undo: async (operation: Operation) => {
      const op = operation as KanbanColumnOperation;
      if (!op.deletedColumn) return;

      await applyOp({
        type: 'add-column',
        columnData: op.deletedColumn,
        columnId: op.deletedColumn.id,
        description: `Undo: ${op.description}`,
        metadata: op.metadata,
        skipHistory: true
      });

      if (Array.isArray(op.columnOrder)) {
        await applyOp({
          type: 'reorder-columns',
          columnOrder: op.columnOrder,
          description: `Undo: ${op.description}`,
          metadata: op.metadata,
          skipHistory: true
        });
      }

      const migratedTasks = op.migratedTasks;
      if (Array.isArray(migratedTasks) && migratedTasks.length > 0) {
        await applyOp({
          type: 'batch-update',
          updates: migratedTasks.map((task) => ({
            nodeId: task.nodeId,
            data: {
              status: task.previousStatus,
              completionState: task.previousCompletionState,
              progress: task.previousProgress,
              viewState: task.previousViewState || {
                kanban: {
                  columnId: task.previousColumnId,
                  order: task.previousOrder,
                },
              },
            },
          })),
          description: `Undo: ${op.description}`,
          metadata: op.metadata,
          skipHistory: true
        } as NodeOperation);
      }
    },
    redo: async (operation: Operation) => {
      const op = operation as KanbanColumnOperation;
      if (!op.columnId) return;

      const migratedTasks = op.migratedTasks;
      if (Array.isArray(migratedTasks) && migratedTasks.length > 0) {
        await applyOp({
          type: 'batch-update',
          updates: migratedTasks.map((task) => ({
            nodeId: task.nodeId,
            data: {
              status: task.nextStatus,
              completionState: task.nextCompletionState,
              progress: task.nextProgress,
              viewState: task.nextViewState || {
                kanban: {
                  columnId: task.nextColumnId,
                  order: task.nextOrder,
                },
              },
            },
          })),
          description: `Redo: ${op.description}`,
          metadata: op.metadata,
          skipHistory: true
        } as NodeOperation);
      }

      await applyOp({
        type: 'delete-column',
        columnId: op.columnId,
        description: `Redo: ${op.description}`,
        metadata: op.metadata,
        skipHistory: true
      });
    }
  });

  handlers.set('reorder-columns', {
    undo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      const columnOrder = readBeforeColumnOrder(op);
      if (!Array.isArray(columnOrder)) return;

      await applyOp({
        type: 'reorder-columns',
        columnOrder,
        aggregateOrder: op.beforeAggregateOrder,
        description: `Undo: ${op.description}`,
        metadata: op.metadata,
        skipHistory: true
      });
    },
    redo: async (operation: Operation) => {
      const op = operation as NodeOperation;
      if (!Array.isArray(op.columnOrder)) return;

      await applyOp({
        type: 'reorder-columns',
        columnOrder: op.columnOrder,
        aggregateOrder: op.aggregateOrder,
        description: `Redo: ${op.description}`,
        metadata: op.metadata,
        skipHistory: true
      });
    }
  });
}
